import { Channel } from '../client/model/skill';
import { removeLeadingSlash } from '../client/utils';

const rtrim = (char: string, url: string) => {
    return url.endsWith(char) ? url.slice(0, -1) : url;
};

interface CreateRouteParams {
    aliceStandalone: string;
    applicationUrlRoot: string;
    documentationUrlRoot: string;
    documentationUrlRootYango: string;
    assetsRoot: string;
}

const ALICE_APP_ROUTE = {
    [Channel.AliceSkill]: 'skills',
    [Channel.AutoApp]: 'auto-apps',
    [Channel.SmartHome]: 'smart-home',
    [Channel.TvApp]: 'tv-apps',
    [Channel.SmartScreenApp]: 'smart-screen',
    [Channel.Thereminvox]: 'thereminvox',
} as const;

export const createRoutes = (params: CreateRouteParams) => {
    const appRoot = rtrim('/', params.applicationUrlRoot);
    const docRoot = rtrim('/', params.documentationUrlRoot);
    const docRootYango = rtrim('/', params.documentationUrlRootYango);
    const assetsRoot = rtrim('/', params.assetsRoot);

    const skillPageRouteConstructor = (
        draft: boolean = false,
        type: string = '',
        idTemplate: string = ':skillId',
        rootSkillPath: string = 'skills',
    ) => {
        const parts = [rootSkillPath, idTemplate, draft && 'draft', type].filter(Boolean);
        parts.unshift(appRoot);
        return parts.join('/');
    };

    return {
        app: {
            root: appRoot,
            assets: (asset: string, root: string = assetsRoot) => `${root}/assets/${asset}`,
            api: (route: string, queryString: string = '') =>
                `${appRoot}/api/${removeLeadingSlash(route)}${queryString}`,
            appStoreApi: (route: string, queryString: string = '') =>
                `${appRoot}/app-store-api/${removeLeadingSlash(route)}${queryString}`,
            skills: `${appRoot}/skills`,
            aliceApps: `${appRoot}/alice-apps`,
            aliceAgentApp: (skillId: string = ':skillId', isDraft: boolean = true, section: string = 'settings') =>
                `${appRoot}/alice-apps/internal/${skillId}/${isDraft ? 'draft' : 'published'}/${section}`,
            aliceExternalApp: (skillId: string = ':skillId', isDraft: boolean = true, section: string = 'settings') =>
                `${appRoot}/alice-apps/external/${skillId}/${isDraft ? 'draft' : 'published'}/${section}`,
            aliceAgentAppSharing: (skillId: string = ':skillId') => `${appRoot}/alice-apps/internal/${skillId}/sharing`,
            aliceExternalAppSharing: (skillId: string = ':skillId') =>
                `${appRoot}/alice-apps/external/${skillId}/sharing`,

            installableApp: (channel: Channel, skillId: string = ':skillId') =>
                `${appRoot}/${ALICE_APP_ROUTE[channel]}/${skillId}`,
            installableApps: (channel: Channel) => `${appRoot}/${ALICE_APP_ROUTE[channel]}`,
            allApps: `${appRoot}/all-available-apps`,
            allAppsByChannel: (channel: Channel) => `${appRoot}/all-available-apps/${ALICE_APP_ROUTE[channel]}`,
            appPage: (channel: Channel, draft: boolean, type: string, skillId: string = ':skillId') => {
                return skillPageRouteConstructor(draft, type, skillId, ALICE_APP_ROUTE[channel]);
            },
            installableAppSettings: (
                channel: Channel,
                draft: boolean,
                settingsType: string,
                skillId: string,
                anchorId?: string,
            ) => {
                return skillPageRouteConstructor(
                    draft,
                    `settings/${settingsType}${anchorId || ''}`,
                    skillId,
                    ALICE_APP_ROUTE[channel],
                );
            },
            installableAppRevision: (channel: Channel, appId: string, revisionSlug: string) => {
                return skillPageRouteConstructor(false, revisionSlug, appId, ALICE_APP_ROUTE[channel]);
            },
            installableAppSharing: (channel: Channel, appId: string) => {
                return skillPageRouteConstructor(false, 'sharing', appId, ALICE_APP_ROUTE[channel]);
            },
            quickApps: (route?: 'start') => `${appRoot}/quick-apps${route ? `/${route}` : ''}`,

            skill: (skillId: string = ':skillId') => `${appRoot}/skills/${skillId}`,
            skillPage: skillPageRouteConstructor,
            skillSettings: (draft: boolean, settingsType: string, skillId: string) => {
                return skillPageRouteConstructor(draft, `settings/${settingsType}`, skillId);
            },
            moderation: {
                test: (skillId: string = ':skillId') => `${appRoot}/moderation/skills/${skillId}/draft/test`,
            },
            unsubscribe: `${appRoot}/unsubscribe`,
            email: `${appRoot}/email`,
            newSkill: (channel: string = ':channel') => `${appRoot}/new/${channel}`,
            skillRating: (skillId: string = ':skillId') => `${appRoot}/skills/${skillId}/rating`,
            skillResources: (type: 'sounds' | 'images' | '', skillId: string = ':skillId') =>
                `${appRoot}/skills/${skillId}/resources/${type}`,
            settings: (type: '' | 'oauth' = '') => `${appRoot}/settings/${type}`,
            userSettings: () => `${appRoot}/settings`,
            accountLinkingComplete: `${appRoot}/account-linking/redirect`,
            documentation: (docPageName: string) => `${appRoot}/documentation${docPageName ? `/${docPageName}` : ''}`,
        },

        documentation: {
            index: `${docRoot}/dialogs/`,
            aliceSkills: {
                about: `${docRoot}/dialogs/alice/doc/about-docpage/`,
                feedback: `${docRoot}/dialogs/alice/doc/feedback-docpage/`,
                generalSettings: `${docRoot}/dialogs/alice/doc/publish-docpage/#general`,
                publicationSettings: `${docRoot}/dialogs/alice/doc/publish-docpage/#publish`,
                yaCloud: `${docRoot}/dialogs/alice/doc/ycloud-promo-docpage/`,
                webhookUrl: `${docRoot}/dialogs/alice/doc/publish-docpage/#general__webhook-url`,
                considerations: `${docRoot}/dialogs/alice/doc/development-docpage/#considerations`,
                structuredExamples: `${docRoot}/dialogs/alice/doc/publish-docpage/#general__field-request-examples`,
                appmetrica: `${docRoot}/dialogs/alice/doc/appmetrica-docpage/`,
                nlu: `${docRoot}/dialogs/alice/doc/nlu-docpage/`,
                stateStorage: `${docRoot}/dialogs/alice/doc/session-persistence-docpage/`,
                rsy: `${docRoot}/dialogs/alice/doc/ads-yandex-docpage/`,
            },
            aliceApps: {
                createApp: 'https://yandex.ru/dev/dialogs/apps/doc/ru/app-lifecycle#step-1',
                voiceControlBeta: 'https://yandex.ru/dev/dialogs/apps/doc/ru/voice-requirements',
                advancedVoiceControlRequirements:
                    'https://yandex.ru/legal/dialogues_requirements/ru/#9-dopolnitelnye-trebovaniya%2C-preduyavlyaemye-k-prilozheniyam-i-ih-kontentu',
                porgVerify: 'https://yandex.ru/dev/dialogs/apps/doc/ru/requirements#company',
                quickAppsAgreements: 'https://yandex.ru/legal/tv_mobile_agreement_smarttv/ru/',
            },
            nda: 'https://wiki.yandex-team.ru/security/nda/',
            smartHome: {
                webhookUrl: `${docRoot}/dialogs/alice/doc/smart-home/reference/check-docpage/`,
                about: `${docRoot}/dialogs/smart-home/doc/ru`,
                trustedSkills: `${docRoot}/dialogs/smart-home/doc/ru/#about-trusted-skills`,
            },
            termsOfUse: 'https://yandex.ru/legal/dialogues_termsofuse/',
            thereminvox: {
                index: `${docRoot}/dialogs/alice/doc/synthesizer/about-docpage/`,
            },
        },
        documentationYango: {
            index: `${docRootYango}/docs`,
            feedback: `${docRootYango}/docs/smart-home/doc/feedback.html`,
        },
        external: {
            aliceStandalone: params.aliceStandalone,
        },
    };
};
